import React, { createContext, useEffect, useRef } from 'react';
import { devtools } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { createStore, type StoreApi } from 'zustand/vanilla';

import { openReplayStateLogger } from '../../../hooks/open-replay';
import type { BaseStepsInterface } from '../data-enrichment/types/dataEnrichmentTypes';
import type { DataInterface } from './file-upload.types';

export type FileUploadDataSetter = (
  update: DataInterface | ((prevData: DataInterface) => DataInterface)
) => void;

export type FileUploadStoreState = DataInterface & {
  steps: BaseStepsInterface[];
  activeStep: number;
  setActiveStep: (step: number) => void;
  goToNext: () => void;
  goToPrevious: () => void;
  isCompleteStep: (stepIndex: number) => boolean;
  setData: FileUploadDataSetter;
};

type FileUploadStore = FileUploadStoreState & {
  baseSteps: BaseStepsInterface[];
  sheetSelectionStep: BaseStepsInterface['content'];
  setBaseSteps: (
    baseSteps: BaseStepsInterface[],
    sheetSelectionStep: BaseStepsInterface['content']
  ) => void;
};

const createFileUploadInitialData = (): DataInterface => ({
  workbook: { Sheets: {}, SheetNames: [] },
  sheetNames: [],
  mappedWorkbook: [[]],
  selectedSheet: '',
  headers: [],
  headerIndex: '',
  combinedData: [{}],
  fileType: 'csv',
  mappedColumns: [],
  oryId: '',
  clientName: '',
  email: '',
  s3FileLocation: '',
});

const buildSteps = ({
  baseSteps,
  sheetSelectionStep,
  sheetNames,
}: {
  baseSteps: BaseStepsInterface[];
  sheetSelectionStep: BaseStepsInterface['content'];
  sheetNames: DataInterface['sheetNames'];
}) => {
  const steps = [...baseSteps];
  if (hasMultipleSheets({ sheetNames })) {
    const sheetsStep: BaseStepsInterface = {
      title: 'Select sheet',
      description: '',
      content: sheetSelectionStep,
    };
    steps.splice(1, 0, sheetsStep);
  }
  return steps;
};

const clampStep = (step: number, max: number) =>
  Math.min(Math.max(step, 0), max - 1);

const pickDataFromState = (state: FileUploadStoreState): DataInterface => ({
  workbook: state.workbook,
  sheetNames: state.sheetNames,
  mappedWorkbook: state.mappedWorkbook,
  selectedSheet: state.selectedSheet,
  headers: state.headers,
  headerIndex: state.headerIndex,
  combinedData: state.combinedData,
  fileType: state.fileType,
  mappedColumns: state.mappedColumns,
  oryId: state.oryId,
  clientName: state.clientName,
  email: state.email,
  s3FileLocation: state.s3FileLocation,
});

const createFileUploadStore = ({
  baseSteps,
  sheetSelectionStep,
}: {
  baseSteps: BaseStepsInterface[];
  sheetSelectionStep: BaseStepsInterface['content'];
}) => {
  const initialData = createFileUploadInitialData();
  return createStore<FileUploadStore>()(
    devtools(
      openReplayStateLogger(
        (set, get) => ({
          ...initialData,
          baseSteps,
          sheetSelectionStep,
          steps: buildSteps({
            baseSteps,
            sheetSelectionStep,
            sheetNames: initialData.sheetNames,
          }),
          activeStep: 0,
          setActiveStep: (step) =>
            set((state) => ({
              activeStep: clampStep(step, state.steps.length),
            })),
          goToNext: () =>
            set((state) => ({
              activeStep: clampStep(state.activeStep + 1, state.steps.length),
            })),
          goToPrevious: () =>
            set((state) => ({
              activeStep: clampStep(state.activeStep - 1, state.steps.length),
            })),
          isCompleteStep: (stepIndex) => stepIndex < get().activeStep,
          setData: (update) =>
            set((state) => {
              const prevData = pickDataFromState(state);
              const nextData =
                typeof update === 'function' ? update(prevData) : update;
              const sheetNames = nextData.sheetNames ?? state.sheetNames;
              const steps = buildSteps({
                baseSteps: state.baseSteps,
                sheetSelectionStep: state.sheetSelectionStep,
                sheetNames,
              });
              return {
                ...state,
                ...nextData,
                steps,
                activeStep: clampStep(state.activeStep, steps.length),
              };
            }),
          setBaseSteps: (nextBaseSteps, nextSheetSelectionStep) =>
            set((state) => {
              const steps = buildSteps({
                baseSteps: nextBaseSteps,
                sheetSelectionStep: nextSheetSelectionStep,
                sheetNames: state.sheetNames,
              });
              return {
                baseSteps: nextBaseSteps,
                sheetSelectionStep: nextSheetSelectionStep,
                steps,
                activeStep: clampStep(state.activeStep, steps.length),
              };
            }),
        }),
        'file_upload'
      ),
      { name: 'FileUpload' }
    )
  );
};

const FileUploadStoreContext = createContext<StoreApi<FileUploadStore> | null>(
  null
);

export const useFileUploadStore = <T,>(
  selector: (state: FileUploadStoreState) => T
) => {
  const store = React.useContext(FileUploadStoreContext);
  if (!store) {
    throw new Error('FileUploadStoreProvider is missing');
  }
  return useStoreWithEqualityFn(store, selector, shallow);
};

export const FileUploadProvider = ({
  children,
  baseSteps,
  sheetSelectionStep,
}: {
  children: React.ReactElement;
  baseSteps: BaseStepsInterface[];
  sheetSelectionStep: BaseStepsInterface['content'];
}) => {
  const storeRef = useRef<StoreApi<FileUploadStore> | null>(null);
  if (!storeRef.current) {
    storeRef.current = createFileUploadStore({ baseSteps, sheetSelectionStep });
  }

  useEffect(() => {
    storeRef.current?.getState().setBaseSteps(baseSteps, sheetSelectionStep);
  }, [baseSteps, sheetSelectionStep]);

  return (
    <FileUploadStoreContext.Provider value={storeRef.current}>
      {children}
    </FileUploadStoreContext.Provider>
  );
};

export const hasMultipleSheets = ({
  sheetNames = [],
}: {
  sheetNames: DataInterface['sheetNames'];
}) => {
  return sheetNames.length > 1;
};
